import { useCallback, useEffect, useRef, useState } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  $isNodeSelection,
  $isElementNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
  TextFormatType,
  ElementFormatType,
  LexicalNode,
  RangeSelection,
} from 'lexical';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $isListNode, ListNode } from '@lexical/list';
import { $isLinkNode, LinkNode } from '@lexical/link';
import { $getNearestNodeOfType, $findMatchingParent } from '@lexical/utils';
import { IconButton } from '@socialchorus/shared-ui-components';
import AlignmentDropdown from './alignment-dropdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import AddLinkModal, {
  AddLinkValues,
  LINK_TEXT_MAX_LENGTH,
} from './add-link-modal';
import AddImageModal, { AddImageValues } from './add-image-modal';
import { $isImageNode } from './image-node';
import { $editLinkByKey, $insertNewLink } from './link-helpers';
import { $editImageByKey, $insertNewImage } from './image-helpers';

interface ActiveState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  listType: 'bullet' | 'number' | null;
  elementFormat: ElementFormatType;
}

// Walks up from a node (inclusive) to find the link it lives in, if any.
function $getEnclosingLinkNode(node: LexicalNode): LinkNode | null {
  let current: LexicalNode | null = node;
  while (current) {
    if ($isLinkNode(current)) return current;
    current = current.getParent();
  }
  return null;
}

function ToolbarButton({
  onClick,
  iconName,
  isActive = false,
  disabled = false,
  title = '',
}: {
  onClick: () => void;
  iconName: string;
  isActive?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <IconButton
      onClick={onClick}
      size="extra-compact"
      iconName={iconName}
      title={title}
      disabled={disabled}
      className={isActive ? 'lexical-toolbar__btn--active' : ''}
    />
  );
}

export default function RichTextEditorToolbar() {
  const [editor] = useLexicalComposerContext();
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [linkInitial, setLinkInitial] = useState({ text: '', url: '' });
  const [imageInitial, setImageInitial] = useState({ url: '', altText: '' });
  // Set when opening the modal on top of an existing link, so submit edits that
  // link in place rather than inserting a new one.
  const editingLinkKeyRef = useRef<string | null>(null);
  // Same idea for an image that is selected when the image modal is opened.
  const editingImageKeyRef = useRef<string | null>(null);
  // The selection captured when the modal opened — restored on submit because
  // focusing the modal's inputs clears the editor's live selection.
  const savedSelectionRef = useRef<RangeSelection | null>(null);
  const [activeState, setActiveState] = useState<ActiveState>({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    listType: null,
    elementFormat: '' as ElementFormatType,
  });

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    const anchorNode = selection.anchor.getNode();
    const parent = anchorNode.getParent();

    setActiveState({
      bold: selection.hasFormat('bold'),
      italic: selection.hasFormat('italic'),
      underline: selection.hasFormat('underline'),
      strikethrough: selection.hasFormat('strikethrough'),
      elementFormat: (() => {
        let matchingParent;
        if ($isLinkNode(parent)) {
          matchingParent = $findMatchingParent(
            anchorNode,
            (parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
          );
        }
        if ($isElementNode(matchingParent)) {
          return matchingParent.getFormatType();
        } else if ($isElementNode(anchorNode)) {
          return anchorNode.getFormatType();
        }
        return parent?.getFormatType() || 'left';
      })() as ElementFormatType,
      listType: (() => {
        const listNode = $isListNode(parent)
          ? parent
          : $getNearestNodeOfType(anchorNode, ListNode);
        if (listNode) {
          return listNode.getListType() === 'number' ? 'number' : 'bullet';
        }
        return null;
      })(),
    });
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, updateToolbar]);

  const formatText = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const toggleBulletList = () => {
    if (activeState.listType === 'bullet') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  };

  const toggleNumberedList = () => {
    if (activeState.listType === 'number') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  const formatAlignment = (format: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
  };

  const openLinkModal = () => {
    editingLinkKeyRef.current = null;
    savedSelectionRef.current = null;
    let text = '';
    let url = '';

    editor.read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const linkNode = $getEnclosingLinkNode(selection.anchor.getNode());
      if (linkNode) {
        // Cursor is inside an existing link: pre-fill and edit it in place.
        editingLinkKeyRef.current = linkNode.getKey();
        text = linkNode.getTextContent();
        url = linkNode.getURL();
      } else {
        // Remember the selection so we can linkify it on submit.
        savedSelectionRef.current = selection.clone();
        const selectedText = selection.getTextContent();
        if (
          selectedText.length > 0 &&
          selectedText.length <= LINK_TEXT_MAX_LENGTH
        ) {
          text = selectedText;
        }
      }
    });

    setLinkInitial({ text, url });
    setLinkModalOpen(true);
  };

  const insertLink = ({ text, url }: AddLinkValues) => {
    const trimmedUrl = url.trim();
    const normalizedUrl = /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl.replace(/^\/\//, '')}`;

    const linkText = text || normalizedUrl;
    editor.update(() => {
      const editingKey = editingLinkKeyRef.current;
      if (editingKey) {
        $editLinkByKey(editingKey, linkText, normalizedUrl);
        editingLinkKeyRef.current = null;
        return;
      }

      $insertNewLink(linkText, normalizedUrl, savedSelectionRef.current);
      savedSelectionRef.current = null;
    });
  };

  const openImageModal = () => {
    editingImageKeyRef.current = null;
    let url = '';
    let altText = '';

    editor.read(() => {
      const selection = $getSelection();
      if (!$isNodeSelection(selection)) return;

      // A clicked image is held as a node selection. If one is selected, edit it.
      const imageNode = selection.getNodes().find($isImageNode);
      if (imageNode) {
        editingImageKeyRef.current = imageNode.getKey();
        url = imageNode.getSrc();
        altText = imageNode.getAltText();
      }
    });

    setImageInitial({ url, altText });
    setImageModalOpen(true);
  };

  const insertImage = ({ url, altText }: AddImageValues) => {
    const trimmedUrl = url.trim();
    const normalizedUrl = /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl.replace(/^\/\//, '')}`;

    editor.update(() => {
      const editingKey = editingImageKeyRef.current;
      if (editingKey) {
        $editImageByKey(editingKey, normalizedUrl, altText);
        editingImageKeyRef.current = null;
        return;
      }

      $insertNewImage(normalizedUrl, altText);
    });
  };

  return (
    <div className="lexical-footer" role="toolbar" aria-label="Formatting">
      <ToolbarButton
        onClick={() => formatText('bold')}
        title="Bold"
        iconName="format_bold"
        isActive={activeState.bold}
      />
      <ToolbarButton
        onClick={() => formatText('italic')}
        title="Italic"
        iconName="format_italic"
        isActive={activeState.italic}
      />
      <ToolbarButton
        onClick={() => formatText('underline')}
        title="Underline"
        iconName="format_underlined"
        isActive={activeState.underline}
      />
      <ToolbarButton
        onClick={() => formatText('strikethrough')}
        isActive={activeState.strikethrough}
        title="Strikethrough"
        iconName="format_strikethrough"
      />

      <span className="lexical-toolbar__separator" />

      <ToolbarButton onClick={openLinkModal} title="Link" iconName="link" />
      <ToolbarButton onClick={openImageModal} title="Image" iconName="image" />

      <span className="lexical-toolbar__separator" />

      <AlignmentDropdown
        value={activeState.elementFormat}
        onChange={formatAlignment}
      />
      <ToolbarButton
        onClick={toggleBulletList}
        isActive={activeState.listType === 'bullet'}
        title="Bullet list"
        iconName="format_list_bulleted"
      />
      <ToolbarButton
        onClick={toggleNumberedList}
        isActive={activeState.listType === 'number'}
        title="Numbered list"
        iconName="format_list_numbered"
      />

      <AddLinkModal
        open={linkModalOpen}
        onOpenChange={setLinkModalOpen}
        onSubmit={insertLink}
        initialText={linkInitial.text}
        initialUrl={linkInitial.url}
      />
      <AddImageModal
        open={imageModalOpen}
        onOpenChange={setImageModalOpen}
        onSubmit={insertImage}
        initialUrl={imageInitial.url}
        initialAltText={imageInitial.altText}
      />
    </div>
  );
}
